/**
 * Довідковий центр `/help` — зведена сторінка (ч.972, етап 1).
 *
 * Макет за зразком власника (ч.971): три колонки — розділи з лічильниками,
 * список назв з фільтром за видом матеріалу (ч.1420), текст статті + оцінка
 * «Стаття була корисною?».
 * Розділи = розділи МЕНЮ системи (ч.973): користувач шукає довідку там
 * само, де працює. Питання-відповіді сюди не потрапляють: вони лише для
 * агентів (ч.1419, рішення власника).
 * Сторінка ПУБЛІЧНА: працює без логіну, щоб клієнт знаходив відповідь до
 * звернення у підтримку.
 *
 * Вибір статті змінює адресу (`/help/<slug>`) — щоб посилання на статтю
 * можна було переслати.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useAuth } from '@/auth/store';
import { help, type HelpArticleListItem } from '@/api/help';

import { articleSections, renderMarkdown } from './markdown';

const ALL = '__all__';
// ч.1420: види матеріалів у порядку показу у фільтрі. Питань немає: вони лише для агентів.
const KINDS: { kind: string; label: string }[] = [
  { kind: ALL, label: 'Усі' },
  { kind: 'topic', label: 'Статті' },
  { kind: 'page_doc', label: 'Документи' },
  { kind: 'page_ref', label: 'Сторінки' },
];

export default function HelpPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const guest = !useAuth((a) => a.sessionActive);
  const token = useAuth((st) => st.sessionActive);
  const location = useLocation();
  const [sp] = useSearchParams();
  // ч.1117: запит із пошуку в шапці приходить в адресі (`/help?q=…`). Раніше
  // сторінка його не читала — «усі результати у довіднику» відкривало довідку
  // без запиту, і шукати доводилось удруге.
  const urlQ = sp.get('q') ?? '';
  const [section, setSection] = useState<string>(ALL);
  const [kind, setKind] = useState<string>(ALL);
  const [q, setQ] = useState(urlQ);
  const [query, setQuery] = useState(urlQ.trim());
  const [rated, setRated] = useState<string | null>(null);

  // Новий запит із шапки, коли довідка вже відкрита.
  useEffect(() => {
    if (urlQ) { setQ(urlQ); setQuery(urlQ.trim()); setSection(ALL); setKind(ALL); }
  }, [urlQ]);

  // Пошук із затримкою — не смикаємо сервер на кожну літеру.
  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const sections = useQuery({
    queryKey: ['help-sections'],
    queryFn: () => help.sections(),
    staleTime: 10 * 60 * 1000,
  });

  const list = useQuery({
    queryKey: ['help-articles', section, query],
    queryFn: () => help.articles({
      section: section === ALL || query ? undefined : section,
      q: query || undefined,
    }),
    staleTime: 5 * 60 * 1000,
  });

  const article = useQuery({
    queryKey: ['help-article', slug],
    queryFn: () => help.article(slug!),
    enabled: !!slug,
  });

  // ч.1420: вид фільтрується тут, а не на сервері: лічильники біля видів рахуються з того самого
  // списку, тож показують, скільки матеріалів саме в обраному розділі чи результатах пошуку.
  const found = useMemo(() => list.data ?? [], [list.data]);
  const items = useMemo(
    () => (kind === ALL ? found : found.filter((a) => a.kind === kind)),
    [found, kind],
  );
  const kindCount = (k: string) => found.filter((a) => a.kind === k).length;

  // Журнал пошуку: запит без результату — сигнал, що статті бракує.
  const logged = useRef<string>('');
  useEffect(() => {
    if (!query || list.isLoading || logged.current === query) return;
    logged.current = query;
    void help.logSearch(query, (list.data?.length ?? 0) > 0);
  }, [query, list.isLoading, list.data]);

  // Перша стаття відкривається сама — щоб права колонка не була порожньою.
  useEffect(() => {
    if (!slug && items.length > 0) {
      // Запит з адреси лишаємо: автовідкриття першої статті не має його стерти.
      navigate({ pathname: `/help/${items[0]!.slug}`, search: location.search },
        { replace: true });
    }
  }, [slug, items, navigate, location.search]);

  // Нова стаття читається з початку, а не з місця, де зупинились у попередній.
  const articleBox = useRef<HTMLElement>(null);
  useEffect(() => {
    setRated(null);
    if (articleBox.current) articleBox.current.scrollTop = 0;
  }, [slug]);

  const total = useMemo(
    () => (sections.data ?? []).reduce((s, x) => s + x.count, 0),
    [sections.data],
  );

  const bodyHtml = useMemo(
    () => (article.data ? renderMarkdown(article.data.body_md) : ''),
    [article.data],
  );
  // ч.1336 (рішення власника): статті сторінок не ріжуться на питання — над довгою статтею
  // зміст із переходом до розділу.
  const toc = useMemo(
    () => (article.data ? articleSections(article.data.body_md) : []),
    [article.data],
  );

  const pick = (a: HelpArticleListItem) => navigate(`/help/${a.slug}`);

  // ч.1429: заголовок вікна і опис — свої у кожної статті (пошуковик читає їх після виконання сторінки).
  useEffect(() => {
    const a = article.data;
    if (!a) return undefined;
    const before = document.title;
    document.title = `${a.title} — довідка Облік+`;
    const meta = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (meta && a.summary) meta.content = a.summary;
    return () => { document.title = before; };
  }, [article.data]);

  // Посилання в тексті статті — перехід усередині застосунку, без перезавантаження сторінки.
  const followLink = (e: MouseEvent<HTMLElement>) => {
    const href = (e.target as HTMLElement).closest('a')?.getAttribute('href');
    if (href?.startsWith('/help/')) {
      e.preventDefault();
      navigate(href);
    }
  };

  return (
    <div className="help">
      <div className="help__head">
        <div>
          <div className="help__title">Як ми можемо допомогти?</div>
          <div className="help__subtitle">
            Знайдіть відповідь про роботу системи або відкрийте потрібний розділ.
          </div>
        </div>
        <div className="help__search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Пошук у довідці"
            aria-label="Пошук у довідці"
          />
        </div>
      </div>

      <div className="help__body">
        <aside className="help__sections">
          <div className="help__col-title">Розділи</div>
          <button type="button"
            className={`help__section${section === ALL ? ' active' : ''}`}
            onClick={() => { setSection(ALL); setQ(''); }}>
            <span>Усі розділи</span><span className="help__count">{total}</span>
          </button>
          {(sections.data ?? []).map((s) => (
            <button key={s.section} type="button"
              className={`help__section${section === s.section ? ' active' : ''}`}
              onClick={() => { setSection(s.section); setQ(''); }}>
              <span>{s.section}</span><span className="help__count">{s.count}</span>
            </button>
          ))}
        </aside>

        <div className="help__list">
          {/* Вузький екран: колонка розділів схована — вибір випадашкою. */}
          <div className="help__mobile-sections">
            <select value={section} onChange={(e) => { setSection(e.target.value); setQ(''); }}
              aria-label="Розділ довідки">
              <option value={ALL}>Усі розділи ({total})</option>
              {(sections.data ?? []).map((s) => (
                <option key={s.section} value={s.section}>{s.section} ({s.count})</option>
              ))}
            </select>
          </div>
          <div className="help__col-title">
            {query ? 'Результати пошуку' : section === ALL ? 'Усі розділи' : section}
          </div>
          <div className="help__kinds" role="radiogroup" aria-label="Вид матеріалу">
            {KINDS.map((k) => {
              const count = k.kind === ALL ? found.length : kindCount(k.kind);
              return (
                <button key={k.kind} type="button" role="radio" aria-checked={kind === k.kind}
                  className={`help__kind${kind === k.kind ? ' active' : ''}`}
                  disabled={count === 0 && kind !== k.kind}
                  onClick={() => setKind(k.kind)}>
                  {k.label}<span className="help__count">{count}</span>
                </button>
              );
            })}
          </div>
          <div className="help__cards">
            {list.isLoading && <div className="help__muted">Завантаження…</div>}
            {!list.isLoading && found.length > 0 && items.length === 0 && (
              <div className="help__muted">
                Матеріалів цього виду тут немає —{' '}
                <button type="button" className="help__link" onClick={() => setKind(ALL)}>
                  показати всі
                </button>.
              </div>
            )}
            {!list.isLoading && found.length === 0 && (
              <div className="help__muted">
                Нічого не знайдено. Спробуйте інші слова
                {/* ч.1009: звернутись у підтримку може лише залогінений —
                    гостю кидати посилання на закриту сторінку не можна. */}
                {token ? (
                  <> — або <button type="button" className="help__link"
                    onClick={() => navigate('/support')}>
                    зверніться у підтримку
                  </button>.</>
                ) : '.'}
              </div>
            )}
            {items.map((a) => (
              <a key={a.slug} href={`/help/${a.slug}`}
                className={`help__card${a.slug === slug ? ' active' : ''}`}
                onClick={(e) => { e.preventDefault(); pick(a); }}>
                <div className="help__card-title">{a.title}</div>
              </a>
            ))}
          </div>
        </div>

        <article className="help__article" ref={articleBox}>
          {article.isLoading && <div className="help__muted">Завантаження…</div>}
          {!article.isLoading && !article.data && (
            // ч.997: за прямим посиланням на прибрану статтю показуємо
            // зрозуміле пояснення, а не «оберіть статтю» — людина вже
            // обрала, просто її більше немає.
            <div className="help__muted">
              {slug
                ? 'Такої статті немає — можливо, її обʼєднали з іншою. '
                  + 'Скористайтесь пошуком або оберіть статтю зі списку.'
                : 'Оберіть статтю зі списку.'}
            </div>
          )}
          {article.data && (
            /* ч.977: обгортка з власною шириною — щоб текст керувався
               ОДНИМ місцем, а не набором max-width на кожному елементі.
               ч.1002 (вимога власника): + `card` — стаття у білому блоці,
               як таблиці на сторінках-списках (Закон дизайну #5). */
            <div className="help__article-inner card">
              <nav className="help__crumbs" aria-label="Шлях">
                {guest && <><a href="/">Облік+</a> / </>}
                <a href="/help" onClick={(e) => { e.preventDefault(); navigate('/help'); }}>Довідка</a>
                {' / '}<span>{article.data.section}</span>
              </nav>
              <h1 className="help__article-title">{article.data.title}</h1>
              {article.data.summary && <p className="help__lead">{article.data.summary}</p>}
              {toc.length >= 4 && (
                <nav className="help__toc" aria-label="Зміст статті">
                  <div className="help__toc-title">Зміст</div>
                  <ol>
                    {toc.map((sec) => (
                      <li key={sec.id}>
                        <a href={`#${sec.id}`} onClick={(e) => {
                          e.preventDefault();
                          document.getElementById(sec.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}>{sec.title}</a>
                      </li>
                    ))}
                  </ol>
                </nav>
              )}
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              <div className="help-md" onClick={followLink}
                // Вміст екранується у renderMarkdown: HTML зі статті вставити
                // неможливо (див. markdown.ts).
                dangerouslySetInnerHTML={{ __html: bodyHtml }} />

              {(article.data.related?.length ?? 0) > 0 && (
                <section className="help__related" aria-label="Схожі статті">
                  <h2>Схожі статті</h2>
                  <ul>
                    {article.data.related!.map((r) => (
                      <li key={r.slug}>
                        <a href={`/help/${r.slug}`} onClick={(e) => { e.preventDefault(); navigate(`/help/${r.slug}`); }}>{r.title}</a>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {guest && article.data.landing && (
                <p className="help__landing"><a href={article.data.landing.path}>{article.data.landing.label}</a></p>
              )}

              <div className="help__rate">
                <span>Стаття була корисною?</span>
                {rated ? (
                  <span className="help__rate-done">
                    {rated === 'yes' ? 'Дякуємо за відгук!' : 'Дякуємо — ми допишемо цю статтю.'}
                  </span>
                ) : (
                  <span className="help__rate-btns">
                    <button type="button" onClick={() => {
                      setRated('yes'); void help.feedback(article.data!.slug, true);
                    }}>так</button>
                    <button type="button" onClick={() => {
                      setRated('no'); void help.feedback(article.data!.slug, false);
                    }}>ні</button>
                  </span>
                )}
              </div>
            </div>
          )}
        </article>
      </div>
    </div>
  );
}
